// The verse, explained once — shared by the in-app /map and the public /site/how.
//
// The whole page reads left → right: the shared groundwork on the outside, your
// private verse on the inside, and a gate of judgment between them. Copy is
// chosen so a newcomer never meets an internal name — only what they do and see.

export type FlowZone = 'groundwork' | 'gate' | 'yours'

export interface FlowStage {
  /** The mark this stage carries across the app. */
  glyph: string
  /** Short, active title — a verb where it can be one. */
  name: string
  /** Two- or three-word caption under the title. */
  role: string
  /** One plain sentence, no mechanism names. */
  detail: string
  /** Which side of the gate this stage lives on. */
  zone: FlowZone
  /** Deep-link to the surface, when the stage has one. */
  to?: string
}

// The core loop, in order. The outside world becomes shared groundwork; a signal
// asks; you judge at the gate; what you keep becomes yours and powers real work.
export const FLOW: FlowStage[] = [
  {
    glyph: '◌',
    name: 'The outside world',
    role: 'the raw material',
    detail: 'Public sources: official APIs, open feeds, and the live web.',
    zone: 'groundwork',
  },
  {
    glyph: '●',
    name: 'Springs feed',
    role: 'on a schedule you set',
    detail: 'Each source is gathered on a clock you can watch and pause, and every capture is kept alongside the exact page it came from.',
    zone: 'groundwork',
    to: '/pipelines',
  },
  {
    glyph: '▤',
    name: 'The pool fills',
    role: 'shared groundwork',
    detail: 'Captures become tidy, searchable records: opportunities, awards, documents, articles. Shared groundwork; none of it is yours yet.',
    zone: 'groundwork',
    to: '/ledger',
  },
  {
    glyph: '◈',
    name: 'Signals ask',
    role: 'crossing your rules',
    detail: 'When something crosses the rules you set, it steps forward and asks for your judgment: act on it, dismiss it, or let it sharpen the rule.',
    zone: 'gate',
    to: '/signals',
  },
  {
    glyph: '✳',
    name: 'Stars held',
    role: 'your constellation',
    detail: 'Whatever you keep becomes a star in your constellation, with its full history and trail back to the source intact.',
    zone: 'yours',
    to: '/graph',
  },
  {
    glyph: '◉',
    name: 'Missions act',
    role: 'work with steps',
    detail: 'Your stars power real work: steps, gates, and results that build your track record.',
    zone: 'yours',
    to: '/missions',
  },
  {
    glyph: '◆',
    name: 'Outcomes',
    role: 'won or lost, recorded',
    detail: 'Every mission closes with a verdict that feeds your record and teaches tomorrow’s judgment.',
    zone: 'yours',
    to: '/missions',
  },
]

// The three moments where something becomes more real to you. Plain language:
// what you do and see, never the machinery behind it.
export const THRESHOLDS = [
  {
    step: 'A record exists',
    means: 'Thousands sit in the shared pool. Browse them freely; looking commits you to nothing.',
  },
  {
    step: 'A signal asks',
    means: 'One crossed the rules you set, so it steps forward and waits in ◈ for your judgment.',
  },
  {
    step: 'A star is held',
    means: 'You kept it, so it joined your constellation, with its history, its source, and its place in your world.',
  },
]

// One card per surface. Says what you do there, not how it works underneath.
export const SURFACES: { name: string; to: string; glyph: string; what: string }[] = [
  { name: 'Domains', to: '/domains', glyph: '◐', what: 'The subject areas of your world. Each bundles its own vocabulary, springs, and rules. Build one and see exactly what you get before you commit.' },
  { name: 'Springs', to: '/pipelines', glyph: '●', what: 'What feeds each domain: every source with its schedule, health, and the rules that decide what reaches you. Ask for a new one and the Familiar drafts it for your approval.' },
  { name: 'Ledger', to: '/ledger', glyph: '▤', what: 'Everything in the shared pool, sortable and filterable as a list, board, calendar, or map. One click turns a row into a star or starts a decision.' },
  { name: 'Signals', to: '/signals', glyph: '◈', what: 'What changed and why it matters, routed to the domains that care. What you accept and dismiss quietly tunes what asks next time.' },
  { name: 'Constellation', to: '/graph', glyph: '✳', what: 'Your stars and how they relate: your world model itself, to focus and filter.' },
  { name: 'Missions', to: '/missions', glyph: '◉', what: 'Work with steps, gates, and outcomes. Templates start them, the Familiar runs the steps that carry tools, and each verdict feeds your win rate.' },
  { name: 'The Familiar', to: '/familiar', glyph: '✦', what: 'Your agent. It reads your world and the shared pool, drafts cited work, and proposes — but never decides. The gates stay yours.' },
  { name: 'Trove', to: '/trove', glyph: '▣', what: 'Your own documents and captures. When you add something, it links what it recognises and asks in ◈ about what it doesn’t. Sorting them IS the import.' },
]

// The marks you meet across the app, each as one scannable row.
export const GLYPH_KEY: { glyph: string; label: string }[] = [
  { glyph: '✦', label: 'the Familiar · your agent, on every surface' },
  { glyph: '◈', label: 'a signal waiting for your judgment' },
  { glyph: '✳', label: 'a star you hold · part of your constellation' },
  { glyph: '◇', label: 'a gate · a commitment only you can make' },
  { glyph: '◆', label: 'an outcome you won' },
  { glyph: '▤', label: 'the shared pool of records' },
  { glyph: '▣', label: 'your own documents' },
  { glyph: '● ◐ ◔ ✕', label: 'a spring: flowing · running late · eased off · needs a look' },
]
